import { readWeightAxis, weightRange } from "./fontFile";
import { hasVariableWeight } from "./probe";
import type { LocalFontSlot } from "./settings";
import { FONT_LOAD_TIMEOUT_MS, withTimeout } from "./timing";

export const LOCAL_FONT_FAMILIES: Record<LocalFontSlot, string> = {
	main: "LocalFont",
	light: "LocalFont-light",
	bold: "LocalFont-bold",
};

const VARIABLE_WEIGHT_RANGE = "100 900";

export const faceFamily = (slot: LocalFontSlot, runId: number) => `${LOCAL_FONT_FAMILIES[slot]}-${runId}`;

const faceSources = new WeakMap<FontFace, string>();

export const faceSourceOf = (face: FontFace) => faceSources.get(face) ?? null;

export const loadFace = async (family: string, source: string, descriptors?: FontFaceDescriptors) => {
	const face = new FontFace(family, `url(${JSON.stringify(source)})`, { display: "swap", ...descriptors });
	faceSources.set(face, source);
	await withTimeout(face.load(), FONT_LOAD_TIMEOUT_MS);
	return face;
};

export const loadMainFace = async (family: string, source: string) => {
	const axis = await readWeightAxis(source);
	if (axis) return loadFace(family, source, { weight: weightRange(axis) });
	if (axis === false) return loadFace(family, source);
	const variable = await loadFace(family, source, { weight: VARIABLE_WEIGHT_RANGE });
	document.fonts.add(variable);
	const isVariable = hasVariableWeight(family);
	document.fonts.delete(variable);
	return isVariable ? variable : loadFace(family, source);
};

const REGULAR_WEIGHT = 400;

export const weightSpanOf = (face: FontFace) => {
	const [min = REGULAR_WEIGHT, max = min] = face.weight
		.split(/\s+/)
		.map((part) => (part === "normal" ? REGULAR_WEIGHT : part === "bold" ? 700 : Number(part)))
		.filter((value) => Number.isFinite(value));
	return { min, max };
};

export const renderedWeight = (face: FontFace, wanted: number) => {
	const { min, max } = weightSpanOf(face);
	return min === max ? Math.max(wanted, min) : Math.min(max, Math.max(min, wanted));
};

export const removeFaces = (faces: Iterable<FontFace>) => {
	for (const face of [...faces]) document.fonts.delete(face);
};

const isLocalFace = (face: FontFace) => face.family.replace(/["']/g, "").startsWith(LOCAL_FONT_FAMILIES.main);

export const removeLocalFaces = () => removeFaces([...document.fonts].filter(isLocalFace));
